import React, { useEffect, useState, useMemo } from 'react';
import styles from './ShippingBar.module.scss';
import { useSharedCart } from '../../entrypoints/theme';

// Helper function to remove HTML tags
const stripHtml = (html) => {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || "";
};

// Helper function to format currency
const formatCurrency = (amount) => {
  // Assuming USD for simplicity, adjust as needed for other currencies/locales
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
};

export default function ShippingBar() {
  const { cartState } = useSharedCart();
  const [cartPrice, setCartPrice] = useState(0);
  const freeShippingThreshold = useMemo(() => cartState?.free_shipping_threshold ?? 0, [cartState?.free_shipping_threshold]);

  const isValidThreshold = useMemo(() => freeShippingThreshold > 0, [freeShippingThreshold]);

  useEffect(() => {
    const nvdTotalItem = document.getElementById("shipping-protection-price");
    const cartTotalRaw = cartState?.main_items_total;
    const nvdOptedOut = cartState?.attributes?.nvd_opted_out;

    const cartTotal = parseFloat(cartTotalRaw);
    if (isNaN(cartTotal)) {
        setCartPrice(0);
        return;
    }

    let calculatedPrice = cartTotal;

    if (nvdOptedOut === 0 && nvdTotalItem) {
      const nvdTotal = parseFloat(nvdTotalItem.innerText.replace(/[^0-9.-]+/g,"")) || 0;
      calculatedPrice += nvdTotal;
    }

    setCartPrice(parseFloat(calculatedPrice.toFixed(2)));

  }, [cartState?.main_items_total, cartState?.attributes?.nvd_opted_out]);

  const amountRemaining = useMemo(() =>
    isValidThreshold ? Math.max(0, freeShippingThreshold - cartPrice) : 0,
    [isValidThreshold, freeShippingThreshold, cartPrice]
  );

  const progressPercentage = useMemo(() =>
    isValidThreshold ? Math.min(100, (cartPrice / freeShippingThreshold) * 100) : 0,
    [isValidThreshold, cartPrice, freeShippingThreshold]
  );

  const hasReachedFreeShipping = useMemo(() =>
    isValidThreshold && progressPercentage >= 100,
    [isValidThreshold, progressPercentage]
  );

  const metThresholdMessage = useMemo(() =>
    stripHtml(cartState?.free_shipping_met_threshold_message),
    [cartState?.free_shipping_met_threshold_message]
  );

  const belowThresholdMessage = useMemo(() => {
    const rawMessage = cartState?.free_shipping_below_threshold_message || '';
    const formattedAmount = formatCurrency(amountRemaining);
    return stripHtml(rawMessage.replace('[amount]', formattedAmount));
  }, [cartState?.free_shipping_below_threshold_message, amountRemaining]);

  if (!isValidThreshold) {
    return null;
  }
  
  return (
    <div className={styles.shippingBarContainer}>
      <div className={styles.shippingText}>
        {hasReachedFreeShipping
          ? metThresholdMessage
          : belowThresholdMessage
        }
      </div>

      <div className={styles.progressLabels}>
        <span>$0</span>

        <div className={styles.progressBarBackground}>
          <div
            className={styles.progressBarFill}
            style={{ width: `${progressPercentage}%` }}
            aria-valuenow={progressPercentage}
            aria-valuemin="0"
            aria-valuemax="100"
            role="progressbar"
            aria-label="Shipping progress"
          />
        </div>

        <span>${freeShippingThreshold}</span>
      </div>
    </div>
  );
}
